import { Command } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/command_pb';
import { create } from '@bufbuild/protobuf';
import { MetricType } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/metrics_pb';
import {
  Device,
  DeviceSchema,
  Device_CommissioningState,
  Device_Mode,
  Device_OTAState,
  ProductType,
} from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/system_pb';
import { t } from 'i18next';
import { ResourceNameIdConverter } from '~/shared/models/ResourceNameIdConverter';
import { PeripheralModel } from '~/shared/models/system/PeripheralModel';

export class DeviceModel {
  readonly name: string;

  readonly id: string;

  readonly availableMetricTypes: MetricType[];

  readonly commissioningState: Device_CommissioningState;

  readonly peripherals: PeripheralModel[];

  readonly requestMeasurementCommand: Command | undefined;

  readonly maintenanceModeCommand: Command | undefined;

  readonly mode: Device_Mode;

  readonly title: string | undefined;

  readonly isLinked: boolean;

  readonly allowedProductTypesLinked: Map<ProductType, boolean>;

  readonly updatingFirmwareOTA: boolean;

  readonly allowMaintenanceMode: boolean;

  readonly enableDemoModeCommand: Command | undefined;

  readonly disableDemoModeCommand: Command | undefined;

  readonly supportsDemoMode: boolean;

  readonly isInDemoMode: boolean;

  constructor({
    name,
    availableMetricTypes,
    commissioningState,
    peripherals,
    availableCommands,
    title,
    mode,
    otaState,
    allowedProductTypesLinked,
  }: Device) {
    this.name = name;
    this.id = ResourceNameIdConverter.deviceNameToId(name);
    this.title = title || undefined;
    this.availableMetricTypes = availableMetricTypes;
    this.commissioningState = commissioningState;
    this.maintenanceModeCommand = availableCommands.find(
      (command) => command.payload.case === 'maintenanceMode',
    );
    this.requestMeasurementCommand = availableCommands.find(
      (command) => command.payload.case === 'requestMeasurement',
    );
    this.mode = mode;
    this.peripherals = peripherals.map(
      (peripheral) => new PeripheralModel(peripheral),
    );

    this.isLinked =
      commissioningState !== Device_CommissioningState.READY_FOR_LINKING;

    this.allowMaintenanceMode =
      !!this.maintenanceModeCommand &&
      commissioningState === Device_CommissioningState.OPERATIONAL;

    this.updatingFirmwareOTA = otaState === Device_OTAState.OTA_STATE_UPDATING;

    this.allowedProductTypesLinked = new Map(
      allowedProductTypesLinked.map((value) => [value, true]),
    );

    this.enableDemoModeCommand = availableCommands.find(
      (command) =>
        command.payload.case === 'deviceSpecificCommand' &&
        command.payload.value.id === 'PVDemoModeEnable',
    );

    this.disableDemoModeCommand = availableCommands.find(
      (command) =>
        command.payload.case === 'deviceSpecificCommand' &&
        command.payload.value.id === 'PVDemoModeDisable',
    );

    this.supportsDemoMode =
      !!this.enableDemoModeCommand || !!this.disableDemoModeCommand;

    this.isInDemoMode = !!this.disableDemoModeCommand;
  }

  static createEmpty(): DeviceModel {
    return new DeviceModel(
      create(DeviceSchema, {
        name: '/devices/empty',
        title: t('systemDesign.product.edit.fields.labels.notConnected'),
        commissioningState: Device_CommissioningState.READY_FOR_LINKING,
      }),
    );
  }
}
